import { z } from 'zod';
import { TreeDeviceSchema } from '../../devices/schemas/devices.schemas.ts';
import {
  createPositiveNumberStringSchema,
  hasMinLength,
  hasMaxLength,
  ValidationMessages,
  isValidLongitude,
  isValidLatitude,
  isValidPhoneNumber,
  isValidNumber,
  isPositiveNumber,
} from '@/utils/validation-utils.ts';

const FIELD_LIMITS = {
  PLANT_NAME: { min: 2, max: 30 },
  SHORT_DESCRIPTION: { max: 250 },
} as const;

export const SETUP_TYPES = {
  PRODUCTION_ONLY: 'production-only',
  PRODUCTION_STORAGE: 'production-storage',
  STORAGE_ONLY: 'storage-only',
} as const;

export const PLANT_STATUS = [
  'Operational',
  'Planning',
  'Under Construction',
  'Maintenance',
  'Archived',
] as const;

const basicDetailsFormSchema = z.object({
  name: z
    .string({ error: ValidationMessages.required('Plant name') })
    .refine(
      hasMinLength(FIELD_LIMITS.PLANT_NAME.min),
      ValidationMessages.minLength('Plant name', FIELD_LIMITS.PLANT_NAME.min)
    )
    .refine(
      hasMaxLength(FIELD_LIMITS.PLANT_NAME.max),
      ValidationMessages.maxLength('Plant name', FIELD_LIMITS.PLANT_NAME.max)
    ),
  type: z
    .string({ error: ValidationMessages.required('Plant type') })
    .min(1, { message: ValidationMessages.required('Plant type') }),
  status: z.enum(PLANT_STATUS),
  shortDescription: z
    .string({ error: ValidationMessages.required('Short description') })
    .refine(
      hasMaxLength(FIELD_LIMITS.SHORT_DESCRIPTION.max),
      ValidationMessages.maxLength('Short description', FIELD_LIMITS.SHORT_DESCRIPTION.max)
    )
    .optional(),
});

const siteInformationFormSchema = z.object({
  image: z.array(z.instanceof(File)).optional().or(z.string()),
  country: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('Country'),
    }
  ),
  state: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('State'),
    }
  ),
  city: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('City'),
    }
  ),
  siteAddress: z.string().nullable().optional(),
  coordinates: z.object({
    longitude: z
      .string({ error: ValidationMessages.required('Longitude') })
      .min(1, { message: ValidationMessages.required('Longitude') })
      .refine(isValidLongitude, ValidationMessages.invalidLongitude),
    latitude: z
      .string({ error: ValidationMessages.required('Latitude') })
      .min(1, { message: ValidationMessages.required('Latitude') })
      .refine(isValidLatitude, ValidationMessages.invalidLatitude),
  }),
  timezone: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('Timezone'),
    }
  ),
  siteArea: createPositiveNumberStringSchema({
    fieldName: 'Site area',
    required: false,
  })
    .nullable()
    .optional(),
  siteAccess: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
});

const technicalSpecificationsFormSchema = z
  .object({
    setupType: z.enum([
      SETUP_TYPES.PRODUCTION_STORAGE,
      SETUP_TYPES.PRODUCTION_ONLY,
      SETUP_TYPES.STORAGE_ONLY,
    ]),
    pvCapacity: createPositiveNumberStringSchema({
      fieldName: 'PV Capacity',
      required: false,
    }).nullable(),
    pvInvertingPower: createPositiveNumberStringSchema({
      fieldName: 'PV Inverting Power',
      required: false,
    }).nullable(),
    storageCapacity: createPositiveNumberStringSchema({
      fieldName: 'Storage Capacity',
      required: false,
    }).nullable(),
    storageChemistry: z
      .array(z.number(), { error: 'Storage Chemistry is required' })
      .optional(),
    storageInvertingPower: createPositiveNumberStringSchema({
      fieldName: 'Storage Inverting Power',
      required: false,
    }).nullable(),

    // deprecated - renamed to pvCapacity
    capacity: createPositiveNumberStringSchema({
      fieldName: 'Capacity',
      required: false,
    }),
  })
  .refine(
    (data) => {
      if (data.setupType === SETUP_TYPES.STORAGE_ONLY) return true;

      return (
        data.pvCapacity && isValidNumber(data.pvCapacity) && isPositiveNumber(data.pvCapacity)
      );
    },
    {
      message: 'PV Capacity is required and must be a positive number',
      path: ['pvCapacity'],
    }
  )
  .refine(
    (data) => {
      if (data.setupType === SETUP_TYPES.PRODUCTION_ONLY) return true;

      return (
        data.storageCapacity &&
        isValidNumber(data.storageCapacity) &&
        isPositiveNumber(data.storageCapacity)
      );
    },
    {
      message: 'Storage Capacity is required and must be a positive number',
      path: ['storageCapacity'],
    }
  );

const financialInformationFormSchema = z.object({
  tariffRate: createPositiveNumberStringSchema({
    fieldName: 'Tariff rate',
    required: false,
  }).nullable(),
  expectedROI: createPositiveNumberStringSchema({
    fieldName: 'Expected ROI',
    required: false,
  }).nullable(),
  operationalCosts: createPositiveNumberStringSchema({
    fieldName: 'Operational costs',
    required: false,
  }).nullable(),
});

const contactDetailsFormSchema = z.object({
  owner: z.object({
    firstName: z
      .string({ error: ValidationMessages.required('Owner first name') })
      .min(1, { message: ValidationMessages.required('Owner first name') }),
    lastName: z
      .string({ error: ValidationMessages.required('Owner last name') })
      .min(1, { message: ValidationMessages.required('Owner last name') }),
  }),
  ownerEmail: z
    .string({ error: ValidationMessages.required('Owner email') })
    .email(ValidationMessages.invalidEmail)
    .min(1, { message: ValidationMessages.required('Owner email') }),
  ownerPhoneNumber: z
    .string({ error: ValidationMessages.required('Owner phone number') })
    .min(1, { message: ValidationMessages.required('Owner phone number') })
    .refine(isValidPhoneNumber, ValidationMessages.invalidPhone),

  installer: z
    .string({ error: ValidationMessages.required('Installer name') })
    .min(1, { message: ValidationMessages.required('Installer name') }),
  installerEmail: z
    .string({ error: ValidationMessages.required('Installer email') })
    .email(ValidationMessages.invalidEmail)
    .min(1, { message: ValidationMessages.required('Installer email') }),
  installerPhoneNumber: z
    .string({ error: ValidationMessages.required('Installer phone number') })
    .min(1, { message: ValidationMessages.required('Installer phone number') })
    .refine(isValidPhoneNumber, ValidationMessages.invalidPhone),
});

export const PlantSchema = z.object({
  plantId: z.string(),
  companyId: z.string(),

  basicDetails: basicDetailsFormSchema,
  siteInformation: siteInformationFormSchema,
  technicalSpecifications: technicalSpecificationsFormSchema,
  equipmentAndConnectionDetails: z.object({
    devices: z.array(TreeDeviceSchema),
  }),
  financialInformation: financialInformationFormSchema,
  contactDetails: contactDetailsFormSchema,

  dataCollectionInterval: z.string(),

  createdAt: z.string(),
  updatedAt: z.string(),

  // Read-only fields from API (for image display in edit mode)
  signedImageUrl: z.string().optional(),
  signedImageExpiresAt: z.string().optional(),
});

export type TPlant = z.infer<typeof PlantSchema>;
